import { Dialog, Modal, ModalOverlay } from "@admin-ui/components/primitives/application/modals/modal";
import { Button } from "@admin-ui/components/primitives/base/buttons/button";
import { CloseButton } from "@admin-ui/components/primitives/base/buttons/close-button";
import { Checkbox } from "@admin-ui/components/primitives/base/checkbox/checkbox";
import { Input } from "@admin-ui/components/primitives/base/input/input";
import { FeaturedIcon } from "@admin-ui/components/primitives/foundations/featured-icon";
import { AlertTriangle, Trash01 } from "@untitledui/icons";
import { useState } from "react";
import { Heading as AriaHeading } from "react-aria-components";

const PURGE_PHRASE = "PURGE";

export interface DeleteEntityDialogProps {
  /** Lowercase noun for the entity, e.g. "user" or "account". */
  entityLabel: string;
  /** Display name shown in the prompt; null falls back to no name. */
  entityName: string | null;
  /** What a standard delete will do, from the serializer's deletion_effect. */
  deletionEffect: "delete" | "anonymize";
  /** Count of legally-retained commerce records (drives the anonymize copy). */
  commerceCount: number;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (force: boolean) => void;
}

/**
 * Confirmation dialog for admin deletion of a user or account. Previews the
 * standard outcome (hard delete vs anonymize) and offers a force-purge override
 * — off by default, error-styled, and gated behind typing PURGE — for clearing
 * test data including commerce records.
 */
export function DeleteEntityDialog({
  entityLabel,
  entityName,
  deletionEffect,
  commerceCount,
  isOpen,
  onOpenChange,
  onConfirm,
}: DeleteEntityDialogProps) {
  const [force, setForce] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setForce(false);
      setConfirmText("");
    }
    onOpenChange(open);
  };

  const canConfirm = !force || confirmText === PURGE_PHRASE;

  const standardMessage =
    deletionEffect === "anonymize"
      ? `This ${entityLabel} has ${commerceCount} commerce record${commerceCount === 1 ? "" : "s"}. Personal data will be removed, but those records are retained for legal compliance.`
      : `This ${entityLabel} has no commerce history and will be permanently deleted.`;

  return (
    <ModalOverlay isOpen={isOpen} onOpenChange={handleOpenChange} isDismissable>
      <Modal>
        <Dialog>
          {({ close }) => (
            <div className="pointer-events-auto relative w-full overflow-hidden rounded-2xl bg-primary shadow-xl sm:max-w-100">
              <CloseButton
                onPress={close}
                theme="light"
                size="sm"
                className="absolute top-3 right-3 sm:top-4 sm:right-4"
              />
              <div className="flex flex-col gap-4 px-4 pt-5 sm:px-6 sm:pt-6">
                <div className="relative w-max">
                  <FeaturedIcon
                    color={force ? "error" : "warning"}
                    size="md"
                    theme="modern"
                    icon={force ? AlertTriangle : Trash01}
                  />
                </div>
                <div className="z-10 flex flex-col gap-0.5">
                  <AriaHeading slot="title" className="text-md font-semibold text-primary">
                    {force
                      ? `Force purge ${entityLabel}?`
                      : deletionEffect === "anonymize"
                        ? `Anonymize ${entityLabel}?`
                        : `Delete ${entityLabel}?`}
                  </AriaHeading>
                  <p className="text-sm text-tertiary">
                    {entityName && <span className="font-medium text-secondary">{entityName}</span>}
                    {entityName ? " — " : ""}
                    {force
                      ? "This permanently destroys everything, including commerce records and Stripe data. It cannot be undone — use only for test data."
                      : standardMessage}
                  </p>
                </div>
                <div className="flex flex-col gap-3 rounded-xl bg-error-primary p-3 ring-1 ring-error_subtle ring-inset">
                  <Checkbox
                    isSelected={force}
                    onChange={(selected) => {
                      setForce(selected);
                      if (!selected) setConfirmText("");
                    }}
                    label="Force purge (destroy everything)"
                    hint="Permanently deletes commerce records and Stripe data. Test data only."
                  />
                  {force && (
                    <Input
                      aria-label={`Type ${PURGE_PHRASE} to confirm`}
                      placeholder={`Type ${PURGE_PHRASE} to confirm`}
                      value={confirmText}
                      onChange={setConfirmText}
                    />
                  )}
                </div>
              </div>
              <div className="z-10 flex flex-1 flex-col-reverse gap-3 p-4 pt-6 *:grow sm:grid sm:grid-cols-2 sm:px-6 sm:pt-8 sm:pb-6">
                <Button color="secondary" size="md" onClick={close}>
                  Cancel
                </Button>
                <Button
                  color={force ? "primary-destructive" : "primary"}
                  size="md"
                  isDisabled={!canConfirm}
                  onClick={() => {
                    onConfirm(force);
                    close();
                  }}
                >
                  {force ? "Permanently purge" : deletionEffect === "anonymize" ? "Anonymize" : "Delete"}
                </Button>
              </div>
            </div>
          )}
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
